import * as Sentry from "@sentry/react";
import useLiveLocalStorage from "../../lib/hooks/useLiveLocalStorage";
import {
  FetchResult,
  gql,
  MutationResult,
  QueryHookOptions,
} from "@apollo/client";
import {
  Exact,
  StorefrontGetCartLightQuery,
  StorefrontCartLightFragment,
  useStorefrontGetCartLightQuery,
  useStorefrontGetCartHeavyQuery,
  useStorefrontCartCreateMutation,
  type StorefrontCartHeavyFragment,
  type StorefrontGetCartHeavyQuery,
  type StorefrontAddCartLinesMutation,
  type StorefrontAddCartLinesMutationVariables,
  useStorefrontAddCartLinesMutation,
} from "../MapmakerApi";

gql`
  fragment StorefrontCartLight on StorefrontCart {
    id
    lines(first: 100) {
      edges {
        node {
          quantity
          merchandise {
            ... on StorefrontProductVariant {
              id
            }
          }
        }
      }
    }
  }

  query storefrontGetCartLight($cartId: ID!) {
    storefrontCart: storefrontNode(id: $cartId) {
      id
      ... on StorefrontCart {
        ...StorefrontCartLight
      }
    }
  }

  mutation storefrontCartCreate($input: StorefrontCartInput!) {
    storefrontCartCreate(input: $input) {
      cart {
        ...StorefrontCartHeavy
      }
    }
  }

  mutation storefrontAddCartLines(
    $cartId: ID!
    $lines: [StorefrontCartLineInput!]!
  ) {
    storefrontCartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...StorefrontCartHeavy
      }
    }
  }
`;

const STOREFRONT_CART_ID_KEY = "mapmaker.shopify.cartId";

export function useStorefrontCartId() {
  const [cartId] = useLiveLocalStorage<string>(STOREFRONT_CART_ID_KEY);
  return cartId;
}

export function useClearStorefrontCartId() {
  const [, , clearCartId] = useLiveLocalStorage<string>(STOREFRONT_CART_ID_KEY);
  return clearCartId;
}

function useStorefrontCartIdFull() {
  return useLiveLocalStorage<string>(STOREFRONT_CART_ID_KEY);
}

export function useStorefrontCartLight(
  queryOptions?: Partial<
    QueryHookOptions<
      StorefrontGetCartLightQuery,
      Exact<{
        cartId: string;
      }>
    >
  >
) {
  const cartId = useStorefrontCartId();
  const clearCart = useClearStorefrontCartId();
  const result = useStorefrontGetCartLightQuery({
    ...queryOptions,
    skip: !cartId,
    variables: {
      // @ts-expect-error
      cartId,
    },
    onCompleted: (result) => {
      if (!result.storefrontCart) {
        clearCart();
      }
    },
  });

  const cart =
    (result.data?.storefrontCart as StorefrontCartLightFragment) ?? undefined;

  return {
    cart,
    ...result,
  };
}

export function useStorefrontCartHeavy(
  queryOptions?: Partial<
    QueryHookOptions<
      StorefrontGetCartHeavyQuery,
      Exact<{
        cartId: string;
      }>
    >
  >
) {
  const cartId = useStorefrontCartId();
  const clearCart = useClearStorefrontCartId();
  const result = useStorefrontGetCartHeavyQuery({
    ...queryOptions,
    skip: !cartId,
    variables: {
      // @ts-expect-error
      cartId,
    },
    onCompleted: (result) => {
      if (!result.storefrontCart) {
        clearCart();
      }
    },
  });

  const cart =
    (result.data?.storefrontCart as StorefrontCartHeavyFragment) ?? undefined;

  return {
    cart,
    ...result,
  };
}

export function useStorefrontAddCartLines(): [
  (
    lines: StorefrontAddCartLinesMutationVariables["lines"]
  ) => Promise<FetchResult<StorefrontAddCartLinesMutation>>,
  MutationResult
] {
  const [cartId, setCartId] = useStorefrontCartIdFull();
  const [createCartMutation, createCartStatus] =
    useStorefrontCartCreateMutation();
  const [addCartLinesMutation, addCartLinesStatus] =
    useStorefrontAddCartLinesMutation();

  const createCart = async () => {
    if (cartId) {
      return cartId;
    }

    const result = await createCartMutation({
      variables: {
        input: {
          lines: [],
        },
      },
    });
    const newCart = result.data?.storefrontCartCreate?.cart;
    if (newCart) {
      setCartId(newCart.id);
      return newCart.id;
    } else {
      Sentry.captureException("Error creating cart.", {
        extra: {
          storefrontApiResult: result.data,
          storefrontApiErrors: result.errors,
        },
      });
      throw new Error(
        "Error communicating with shop. Refresh the page or contact us if the problem persists."
      );
    }
  };

  // The method exposed to consumers, accepts line items and adds them to the cart, handling
  // creation if necessary.
  const addMethod = async (
    lines: StorefrontAddCartLinesMutationVariables["lines"]
  ) => {
    const validCartId = cartId ?? (await createCart());
    return await addCartLinesMutation({
      variables: {
        // @ts-expect-error
        cartId: validCartId,
        lines,
      },
    });
  };

  return [
    addMethod,
    {
      ...addCartLinesStatus,
      loading: createCartStatus.loading || addCartLinesStatus.loading,
      error: createCartStatus.error || addCartLinesStatus.error,
      called: createCartStatus.called || addCartLinesStatus.called,
    },
  ];
}
